import { api } from '../api.ts';
import { compact, html, mount, raw, th } from '../html.ts';
import { formatDateTime, options, ROLE, t } from '../i18n.ts';
import { isAdmin, state } from '../state.ts';
import { badge, confirmDialog, emptyState, field, openFormDialog, select, toast, toastError } from '../ui.ts';
import { openWebhookDialog, renderInboundSection } from '../components/connectors.ts';
import { renderSyncSection } from '../components/sync-status.ts';
import { pageHeader, renderActivity } from './common.ts';

export async function activityView(container: HTMLElement): Promise<void> {
  mount(
    container,
    html`${pageHeader(t('Audit'), t('Verlauf'), t('Jede Änderung in der Organisation – unveränderlich protokolliert und Quelle für Webhooks.'))}
    <section class="p-card"><div class="p-card__body" data-activity></div></section>`,
  );
  await renderActivity(container.querySelector('[data-activity]')!, undefined, 100);
}

function secretDialog(title: string, label: string, secret: string, hint: string) {
  openFormDialog({
    title,
    description: hint,
    fields: html`<label class="p-field pma-span-2"><span class="p-label">${label}</span>
      <input class="p-input pma-mono" readonly value="${secret}" data-secret></label>`,
    submitLabel: t('Kopiert, schließen'),
    onOpen: (dialog) => dialog.querySelector<HTMLInputElement>('[data-secret]')?.select(),
    onSubmit: async () => {
      await navigator.clipboard?.writeText(secret).catch(() => {});
    },
  });
}

const LOCALES: Record<string, string> = { de: 'Deutsch', en: 'English' };
const mailChoices = (): Record<string, string> => ({ instant: t('Ungelesene per E-Mail (nach 2 Minuten, gebündelt)'), off: t('Nur in der App') });

function profileCard() {
  const user = state.me!.user;
  return html`<section class="p-card" aria-labelledby="set-profile" id="profile">
    <div class="p-card__header"><h2 id="set-profile">${t('Profil')}</h2>
      <div class="pma-inline-add">
        <button class="p-button p-button--sm" type="button" data-edit-profile>${t('Bearbeiten')}</button>
        <button class="p-button p-button--ghost p-button--sm" type="button" data-change-password>${t('Passwort ändern')}</button>
      </div></div>
    <div class="p-card__body"><dl class="p-description-list" data-p-variant="inline">
      <dt>${t('Name')}</dt><dd>${user.name}</dd>
      <dt>${t('E-Mail')}</dt><dd>${user.email}</dd>
      <dt>${t('Sprache')}</dt><dd>${LOCALES[user.locale] ?? user.locale}</dd>
      <dt>${t('Benachrichtigungen')}</dt><dd>${mailChoices()[user.notification_email] ?? user.notification_email}</dd>
    </dl></div>
    <div class="p-card__footer pma-own-data">
      <div><strong>${t('Deine Daten')}</strong>
        <p class="pma-muted pma-small">${t('Alles, was zu deinem Konto gehört, als JSON-Datei. Projektinhalte exportierst du pro Projekt als CSV.')}</p></div>
      <div class="pma-inline-add">
        <a class="p-button p-button--ghost p-button--sm" href="/api/v1/auth/me/export" download>${t('Meine Daten herunterladen')}</a>
        <button class="p-button p-button--ghost p-button--sm pma-danger-text" type="button" data-delete-account>${t('Konto löschen')}</button>
      </div>
    </div>
  </section>`;
}

interface OrgRef {
  id: string;
  name: string;
}

async function openDeleteAccountDialog() {
  const preview = await api.get<{ blockers: OrgRef[]; deleted: OrgRef[]; left: OrgRef[] }>('/auth/me/deletion');
  const names = (orgs: OrgRef[]) => orgs.map((o) => o.name).join(', ');
  if (preview.blockers.length) {
    openFormDialog({
      title: t('Konto löschen'),
      description: t('Du bist die einzige Inhaberin bzw. der einzige Inhaber von: {orgs}. Übertrage dort zuerst die Inhaberschaft an ein anderes Mitglied (Einstellungen → Mitglieder → Rolle „Inhaber“), dann kannst du dein Konto löschen.', { orgs: names(preview.blockers) }),
      fields: html``,
      submitLabel: t('Verstanden'),
      onSubmit: async () => undefined,
    });
    return;
  }
  openFormDialog({
    title: t('Konto endgültig löschen'),
    danger: true,
    submitLabel: t('Konto löschen'),
    fields: html`<div class="pma-span-2 p-stack" data-p-gap="2">
        ${preview.deleted.length ? html`<p>${t('Gelöscht werden samt allen Inhalten: {orgs}.', { orgs: names(preview.deleted) })}</p>` : ''}
        ${preview.left.length ? html`<p>${t('Du verlässt: {orgs}. Deine Beiträge dort bleiben erhalten und erscheinen als „Gelöschte Person“.', { orgs: names(preview.left) })}</p>` : ''}
        <p class="pma-muted pma-small">${t('Deine API-Tokens und von dir angelegten Konnektoren werden gelöscht. Sicherungen enthalten die Daten noch bis zu 30 Tage. Das lässt sich nicht rückgängig machen.')}</p>
      </div>
      ${field(t('Passwort zur Bestätigung'), 'password', 'password', { required: true, span: true, attrs: 'autocomplete="current-password"' })}`,
    onSubmit: async (data) => {
      await api.delete('/auth/me', { password: data.password });
      state.me = null;
      location.assign('/login');
    },
  });
}

function openProfileDialog() {
  const user = state.me!.user;
  openFormDialog({
    title: t('Profil bearbeiten'),
    fields: html`${field(t('Name'), 'name', 'text', { required: true, span: true, value: user.name })}
      ${select(t('Sprache'), 'locale', options(LOCALES, user.locale), { span: true, hint: t('Gilt für Oberfläche, Benachrichtigungen und Berichte.') })}
      ${select(t('Benachrichtigungen'), 'notification_email', options(mailChoices(), user.notification_email), { span: true, hint: t('Erwähnungen, Zuweisungen und Kommentare zu deinen Aufgaben. E-Mails gehen nur raus, wenn der Server Mail versenden kann.') })}`,
    onSubmit: async (data) => {
      await api.patch('/auth/me', { name: data.name, locale: data.locale, notification_email: data.notification_email });
      // Vocabularies are patched in place, so a locale change needs a fresh page.
      if (data.locale !== user.locale) location.reload();
      else {
        user.name = String(data.name);
        user.notification_email = String(data.notification_email);
        toast(t('Profil gespeichert'));
        window.dispatchEvent(new Event('pma:navigate'));
      }
    },
  });
}

function openPasswordDialog() {
  openFormDialog({
    title: t('Passwort ändern'),
    description: t('Andere angemeldete Geräte werden danach abgemeldet.'),
    fields: html`${field(t('Aktuelles Passwort'), 'current', 'password', { required: true, span: true, attrs: 'autocomplete="current-password"' })}
      ${field(t('Neues Passwort'), 'password', 'password', { required: true, span: true, attrs: 'minlength="10" autocomplete="new-password"', hint: t('Mindestens 10 Zeichen') })}`,
    onSubmit: async (data) => {
      await api.post('/auth/password', { current: data.current, password: data.password });
      toast(t('Passwort geändert'));
    },
  });
}

export async function settingsView(container: HTMLElement): Promise<void> {
  const admin = isAdmin();
  const [org, tokens, hooks, eventTypes, invitations] = await Promise.all([
    api.get('/organization'),
    api.get('/api-tokens'),
    admin ? api.get('/webhooks') : Promise.resolve({ items: [] }),
    api.get('/event-types'),
    admin ? api.get('/organization/invitations') : Promise.resolve({ items: [], mail: false }),
  ]);

  mount(
    container,
    html`${pageHeader(t('Organisation'), t('Einstellungen & Schnittstellen'), t('Profil, Mitglieder, API-Zugänge sowie aus- und eingehende Verbindungen zu Purple Suite, Jira, GitHub, Teams oder eigenen Systemen.'))}
    <div class="pma-stack-lg">
      ${profileCard()}
      <section class="p-card" aria-labelledby="set-org">
        <div class="p-card__header"><h2 id="set-org">${org.name}</h2><span class="pma-muted">${t('Tarif {plan}', { plan: org.plan })}</span>
          ${admin ? html`<button class="p-button p-button--sm" type="button" data-invite>${t('Person einladen')}</button>` : ''}</div>
        <div class="p-card__body"><ul class="pma-list">${org.members.map(
          (m: any) => html`<li class="pma-list__row"><div>${m.name}<div class="pma-muted pma-small">${m.email}</div></div>
            <div class="pma-list__meta">${
              admin && m.user_id !== state.me?.user.id
                ? html`<select class="p-select" data-p-size="sm" data-member-role="${m.user_id}" aria-label="${t('Rolle von {name}', { name: m.name })}">${raw(options(ROLE, m.role))}</select>
                  <button class="p-button p-button--ghost p-button--sm pma-danger-text" type="button" data-remove-member="${m.user_id}">${t('Entfernen')}</button>`
                : badge(ROLE[m.role] ?? m.role)
            }</div></li>`,
        )}</ul>
        ${invitations.items.length ? html`<h3 class="pma-small">${t('Offene Einladungen')}</h3><ul class="pma-list">${invitations.items.map(
          (i: any) => html`<li class="pma-list__row"><div>${i.email}
              <div class="pma-muted pma-small">${ROLE[i.role] ?? i.role}${i.project_keys.length ? ` · ${t('Projekte {keys}', { keys: i.project_keys.join(', ') })}` : ''} · ${i.expired ? html`<span class="pma-overdue">${t('abgelaufen')}</span>` : t('gültig bis {date}', { date: formatDateTime(i.expires_at) })}${i.email_sent_at ? ` · ${t('per E-Mail verschickt')}` : ''}</div></div>
            <button class="p-button p-button--ghost p-button--sm" type="button" data-revoke-invite="${i.id}">${t('Widerrufen')}</button></li>`,
        )}</ul>` : ''}
        <p class="pma-muted pma-small">${t('Einladungen gelten 14 Tage und nur für die eingeladene E-Mail-Adresse. Gäste sehen nur Projekte, die im Projektüberblick für sie freigegeben sind.')}</p></div>
      </section>

      <section class="p-card" aria-labelledby="set-tokens">
        <div class="p-card__header"><h2 id="set-tokens">${t('API-Tokens')}</h2>
          ${admin ? html`<button class="p-button p-button--primary p-button--sm" type="button" data-new-token>${t('Token erstellen')}</button>` : ''}</div>
        <div class="p-card__body">
          <p class="pma-small">${th(t('Tokens authentifizieren Skripte, Agenten und Integrationen per {header}. Die vollständige API ist unter {link} beschrieben.'), {
            header: html`<code>Authorization: Bearer …</code>`,
            link: html`<a href="/api/v1/openapi.json">/api/v1/openapi.json</a>`,
          })}</p>
          ${
            tokens.items.length
              ? html`<ul class="pma-list">${tokens.items.map(
                  (tok: any) => html`<li class="pma-list__row"><div><strong>${tok.name}</strong> <code class="pma-small">${tok.prefix}…</code>
                    <div class="pma-muted pma-small">${tok.scopes.join(', ')} · ${t('von {name}', { name: tok.user_name })} · ${t('zuletzt {when}', { when: formatDateTime(tok.last_used_at) })}</div></div>
                    <div class="pma-list__meta">${tok.revoked_at ? badge(t('Widerrufen')) : admin ? html`<button class="p-button p-button--ghost p-button--sm" type="button" data-revoke="${tok.id}">${t('Widerrufen')}</button>` : badge(t('Aktiv'), 'success')}</div></li>`,
                )}</ul>`
              : emptyState(t('Keine Tokens'), t('Erstelle ein Token, um PMA aus AgentOps, Skripten oder anderen Werkzeugen anzusprechen.'))
          }
        </div>
      </section>

      ${
        admin
          ? html`<section class="p-card" aria-labelledby="set-hooks">
          <div class="p-card__header"><h2 id="set-hooks">${t('Ausgehende Verbindungen')}</h2>
            <button class="p-button p-button--primary p-button--sm" type="button" data-new-hook>${t('Verbindung hinzufügen')}</button></div>
          <div class="p-card__body">
            <p class="pma-small">${th(t('Ereignisse gehen an Teams, Discord, Slack, Jira oder eigene Systeme – mit Vorlagen oder frei definiertem JSON. Zustellung transaktional mit bis zu 8 Wiederholungen; PMA-JSON ist signiert ({signature}).'), {
              signature: html`<code>x-pma-signature: v1=HMAC-SHA256(secret, timestamp + "." + body)</code>`,
            })}</p>
            ${
              hooks.items.length
                ? html`<ul class="pma-list">${hooks.items.map(
                    (h: any) => html`<li class="pma-list__row"><div><strong>${h.name}</strong> ${h.format === 'slack' ? badge('Slack', 'info') : h.format === 'custom' ? badge(`${h.method} · ${t('Vorlage')}`, 'info') : badge('PMA-JSON')} ${h.active ? '' : badge(t('Pausiert'), 'warning')}
                      <div class="pma-muted pma-small pma-break">${h.url}</div>
                      <div class="pma-muted pma-small">${h.event_types.join(', ')} · ${t('{n} zugestellt', { n: h.delivered })}${h.failing ? html` · <span class="pma-overdue">${t('{n} fehlgeschlagen', { n: h.failing })}</span>` : ''}</div></div>
                      <div class="pma-list__meta">
                        <button class="p-button p-button--ghost p-button--sm" type="button" data-test-hook="${h.id}">${t('Testen')}</button>
                        <button class="p-button p-button--ghost p-button--sm" type="button" data-log-hook="${h.id}">${t('Protokoll')}</button>
                        <button class="p-button p-button--ghost p-button--sm" type="button" data-toggle-hook="${h.id}" data-active="${h.active}">${h.active ? t('Pausieren') : t('Aktivieren')}</button>
                        <button class="p-button p-button--ghost p-button--sm pma-danger-text" type="button" data-delete-hook="${h.id}">${t('Löschen')}</button>
                      </div></li>`,
                  )}</ul>`
                : emptyState(t('Keine Webhooks'), t('Schicke Ereignisse wie task.created oder gate.decided an Slack-Brücken, AgentOps oder eigene Dienste.'))
            }
          </div>
        </section>
        <section class="p-card" aria-labelledby="set-inbound" data-inbound></section>
        <section class="p-card" aria-labelledby="set-sync" id="sync" data-sync></section>`
          : ''
      }
      <p class="pma-muted pma-small">${t('Angemeldet als {email} · Rolle {role}', { email: state.me?.user.email, role: ROLE[state.me?.role ?? ''] ?? '–' })}</p>
    </div>`,
  );

  const inbound = container.querySelector<HTMLElement>('[data-inbound]');
  if (inbound) await renderInboundSection(inbound);
  const sync = container.querySelector<HTMLElement>('[data-sync]');
  if (sync) {
    await renderSyncSection(sync);
    if (new URLSearchParams(location.search).get('focus') === 'sync') sync.scrollIntoView({ block: 'start' });
  }

  container.onchange = async (event) => {
    const element = event.target as HTMLSelectElement;
    const userId = element.dataset.memberRole;
    if (!userId) return;
    try {
      await api.patch(`/organization/members/${userId}`, { role: element.value });
      toast(t('Rolle geändert'));
    } catch (error) {
      toastError(error);
      await settingsView(container);
    }
  };

  container.onclick = async (event) => {
    const element = event.target as HTMLElement;
    const reload = () => settingsView(container);
    try {
      if (element.closest('[data-edit-profile]')) openProfileDialog();
      if (element.closest('[data-change-password]')) openPasswordDialog();
      if (element.closest('[data-delete-account]')) await openDeleteAccountDialog();
      if (element.closest('[data-invite]')) {
        const roles = Object.fromEntries(Object.entries(ROLE).filter(([key]) => key !== 'owner'));
        const projects = (await api.get<{ items: any[] }>('/projects')).items;
        openFormDialog({
          title: t('Person einladen'),
          description: invitations.mail
            ? t('PMA schickt den Einladungslink per E-Mail; du kannst ihn zusätzlich kopieren.')
            : t('Ausgehende E-Mail ist noch nicht eingerichtet: Du bekommst den Einladungslink zum Kopieren und gibst ihn selbst weiter.'),
          fields: html`${field(t('E-Mail'), 'email', 'email', { required: true, span: true })}
            ${select(t('Rolle'), 'role', options(roles, 'member'), { span: true, hint: t('Gäste: nur freigegebene Projekte, nur lesen') })}
            <fieldset class="p-fieldset pma-span-2" data-invite-projects hidden><legend class="p-label">${t('Projekte freigeben')}</legend>
              ${
                projects.length
                  ? html`<div class="pma-event-grid">${projects.map(
                      (p) => html`<label class="p-check pma-small"><input type="checkbox" name="project:${p.id}"> <span>${p.key} · ${p.name}</span></label>`,
                    )}</div>`
                  : html`<p class="pma-muted pma-small">${t('Noch keine Projekte.')}</p>`
              }
              <p class="pma-muted pma-small">${t('Der Gast sieht diese Projekte direkt nach der Annahme – welche Bereiche, legst du je Projekt unter „Freigabe für Gäste“ fest.')}</p>
            </fieldset>`,
          submitLabel: t('Einladen'),
          onOpen: (dialog) => {
            const role = dialog.querySelector<HTMLSelectElement>('select[name="role"]')!;
            const projectsBox = dialog.querySelector<HTMLElement>('[data-invite-projects]')!;
            const sync = () => (projectsBox.hidden = role.value !== 'guest');
            role.addEventListener('change', sync);
            sync();
          },
          onSubmit: async (data) => {
            const projectIds = Object.entries(data).filter(([k, v]) => k.startsWith('project:') && v === true).map(([k]) => k.slice(8));
            const created = await api.post('/organization/invitations', {
              email: data.email,
              role: data.role,
              ...(data.role === 'guest' && projectIds.length ? { project_ids: projectIds } : {}),
            });
            await reload();
            secretDialog(
              t('Einladung erstellt'),
              t('Einladungslink'),
              created.url,
              created.email_sent
                ? t('Die E-Mail an {email} ist unterwegs. Der Link wird nur jetzt angezeigt.', { email: created.email })
                : created.email_error
                  ? t('Die E-Mail konnte nicht verschickt werden. Gib den Link selbst weiter; er wird nur jetzt angezeigt.')
                  : t('Gib den Link an {email} weiter; er wird nur jetzt angezeigt.', { email: created.email }),
            );
          },
        });
      }
      const revokeInvite = element.closest<HTMLElement>('[data-revoke-invite]')?.dataset.revokeInvite;
      if (revokeInvite && (await confirmDialog(t('Einladung widerrufen?'), t('Der Link funktioniert danach nicht mehr.'), t('Widerrufen')))) {
        await api.delete(`/organization/invitations/${revokeInvite}`);
        await reload();
      }
      const removeMember = element.closest<HTMLElement>('[data-remove-member]')?.dataset.removeMember;
      if (removeMember && (await confirmDialog(t('Mitglied entfernen?'), t('Die Person verliert sofort den Zugriff auf diese Organisation.'), t('Entfernen')))) {
        await api.delete(`/organization/members/${removeMember}`);
        await reload();
      }
      if (element.closest('[data-new-token]')) {
        openFormDialog({
          title: t('API-Token erstellen'),
          fields: html`${field(t('Name'), 'name', 'text', { required: true, span: true, placeholder: t('z. B. AgentOps') })}
            ${select(t('Rechte'), 'scope', options({ 'read,write': t('Lesen und schreiben'), read: t('Nur lesen') }))}
            ${field(t('Gültig (Tage)'), 'expires_in_days', 'number', { attrs: 'min="1" max="730"', hint: t('Leer = unbefristet') })}`,
          submitLabel: t('Erstellen'),
          onSubmit: async (data) => {
            const created = await api.post('/api-tokens', compact({ name: data.name, scopes: String(data.scope).split(','), expires_in_days: data.expires_in_days }));
            await reload();
            secretDialog(t('Token erstellt'), t('Token'), created.token, t('Das Token wird nur jetzt angezeigt. Bewahre es sicher auf.'));
          },
        });
      }
      const revoke = element.closest<HTMLElement>('[data-revoke]')?.dataset.revoke;
      if (revoke && (await confirmDialog(t('Token widerrufen?'), t('Integrationen mit diesem Token verlieren sofort den Zugriff.'), t('Widerrufen')))) {
        await api.delete(`/api-tokens/${revoke}`);
        await reload();
      }
      if (element.closest('[data-new-hook]')) await openWebhookDialog(eventTypes.items, reload);
      const testId = element.closest<HTMLElement>('[data-test-hook]')?.dataset.testHook;
      if (testId) {
        await api.post(`/webhooks/${testId}/test`);
        toast(t('Test-Ereignis eingereiht'), t('Zustellung erfolgt in wenigen Sekunden.'));
      }
      const toggle = element.closest<HTMLElement>('[data-toggle-hook]');
      if (toggle) {
        await api.patch(`/webhooks/${toggle.dataset.toggleHook}`, { active: toggle.dataset.active !== 'true' });
        await reload();
      }
      const remove = element.closest<HTMLElement>('[data-delete-hook]')?.dataset.deleteHook;
      if (remove && (await confirmDialog(t('Webhook löschen?'), t('Offene Zustellungen werden verworfen.')))) {
        await api.delete(`/webhooks/${remove}`);
        await reload();
      }
      const log = element.closest<HTMLElement>('[data-log-hook]')?.dataset.logHook;
      if (log) {
        const { items } = await api.get(`/webhooks/${log}/deliveries`);
        openFormDialog({
          title: t('Zustellprotokoll'),
          wide: true,
          fields: items.length
            ? html`<div class="p-table-wrap pma-span-2"><table class="p-table pma-table"><thead><tr><th>${t('Ereignis')}</th><th>${t('Status')}</th><th>${t('Versuche')}</th><th>HTTP</th><th>${t('Zeit')}</th></tr></thead>
                <tbody>${items.map(
                  (d: any) => html`<tr><td>${d.event_type}</td><td>${badge(d.status, d.status === 'succeeded' ? 'success' : d.status === 'pending' ? 'info' : 'danger')}</td>
                    <td>${d.attempts}</td><td>${d.last_status_code ?? '–'}${d.last_error ? html`<div class="pma-small pma-muted">${d.last_error}</div>` : ''}</td><td>${formatDateTime(d.updated_at)}</td></tr>`,
                )}</tbody></table></div>`
            : html`<p class="pma-span-2 pma-muted">${t('Noch keine Zustellungen.')}</p>`,
          submitLabel: t('Schließen'),
          onSubmit: async () => {},
        });
      }
    } catch (error) {
      toastError(error);
    }
  };
}
